/* eslint-disable react/display-name */
import React, { memo, useCallback, useMemo } from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { Column, CellProps } from 'react-table';
import isString from 'lodash/isString';
import { MismatchBadge } from './components/MismatchBadge';
import { FlexibilityViewModel } from '../utils/RequisitionDetailsViewModel';
import { GenericDataTable, EmptyPlaceholder } from './components/TableComponents';
import colors from '../../../../../styles/colors';

interface FlexibilityRequirementsProps {
  flexibility: FlexibilityViewModel;
}

interface FlexibilityRow {
  type: string;
  weekday: number | string;
  weekend: number | string;
  weekdayMismatch?: boolean;
  weekendMismatch?: boolean;
}

const display = (n: number) => (n > 0 ? n : <EmptyPlaceholder />);

const hasValue = (val: number | string) => (isString(val) ? val.length > 0 : val > 0);

const displayValue = (val: number | string) => {
  if (isString(val)) return val.length > 0 ? val : <EmptyPlaceholder />;
  return display(val);
};

export const FlexibilityRequirement = memo(({ flexibility }: FlexibilityRequirementsProps) => {
  const { t } = useTranslation(['requisition', 'common']);

  const formatDays = useCallback(
    (days: string[]): string => {
      if (days.length === 0) return '';
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      return days.map((d) => t(`common:days.${d}`) as string).join(', ');
    },
    [t],
  );

  const data = useMemo<FlexibilityRow[]>(
    () => [
      {
        type: t('requisition:mandatoryDays'),
        weekday: formatDays(flexibility.mandatoryDaysWeekday),
        weekend: formatDays(flexibility.mandatoryDaysWeekend),
        weekdayMismatch: flexibility.mandatoryDaysWeekdayMismatch,
        weekendMismatch: flexibility.mandatoryDaysWeekendMismatch,
      },
      {
        type: t('requisition:totalRequiredStartTimes'),
        weekday: flexibility.numOfStartsWeekday,
        weekend: flexibility.numOfStartsWeekend,
        weekdayMismatch: flexibility.numOfStartsWeekdayMismatch,
        weekendMismatch: flexibility.numOfStartsWeekendMismatch,
      },
      {
        type: t('requisition:totalRequiredEndTimes'),
        weekday: flexibility.numOfEndsWeekday,
        weekend: flexibility.numOfEndsWeekend,
        weekdayMismatch: flexibility.numOfEndsWeekdayMismatch,
        weekendMismatch: flexibility.numOfEndsWeekendMismatch,
      },
    ],
    [t, flexibility, formatDays],
  );

  const columns = useMemo<Column<FlexibilityRow>[]>(
    () =>
      [
        {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          Header: t('requisition:requirementType') as string,
          accessor: 'type',
          disableSortBy: true,
          Cell: ({ value, row }: CellProps<FlexibilityRow, string>) => <Box data-testid={`row-label-${row.index}`}>{value}</Box>,
        },
        {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          Header: t('requisition:weekday') as string,
          accessor: 'weekday',
          width: '70px',
          isNumeric: true,
          disableSortBy: true,
          Cell: ({ value, row }: CellProps<FlexibilityRow, number | string>) => (
            <Box
              textAlign={hasValue(value) ? 'right' : 'center'}
              display="inline-flex"
              alignItems="center"
              justifyContent={hasValue(value) ? 'flex-end' : 'center'}
              width="100%"
            >
              <Box data-testid={`cell-weekday-value-${row.index}`}>{displayValue(value)}</Box>
              {row.original.weekdayMismatch && hasValue(value) && (
                <MismatchBadge ml={2} data-testid={`cell-weekday-mismatch-${row.index}`} />
              )}
            </Box>
          ),
        },
        {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          Header: t('requisition:weekend') as string,
          accessor: 'weekend',
          width: '70px',
          isNumeric: true,
          disableSortBy: true,
          Cell: ({ value, row }: CellProps<FlexibilityRow, number | string>) => (
            <Box
              textAlign={hasValue(value) ? 'right' : 'center'}
              display="inline-flex"
              alignItems="center"
              justifyContent={hasValue(value) ? 'flex-end' : 'center'}
              width="100%"
            >
              <Box data-testid={`cell-weekend-value-${row.index}`}>{displayValue(value)}</Box>
              {row.original.weekendMismatch && hasValue(value) && (
                <MismatchBadge ml={2} data-testid={`cell-weekend-mismatch-${row.index}`} />
              )}
            </Box>
          ),
        },
      ] as Column<FlexibilityRow>[],
    [t],
  );

  const getCellBgColor = useCallback((row: FlexibilityRow, columnId: string) => {
    if (columnId === 'weekday' && row.weekdayMismatch && hasValue(row.weekday)) return colors.requisitionDetails.mismatchCellBg;
    if (columnId === 'weekend' && row.weekendMismatch && hasValue(row.weekend)) return colors.requisitionDetails.mismatchCellBg;
    return undefined;
  }, []);

  return (
    <VStack justifyContent="center" alignItems="flex-start" width="100%" py={2} data-testid="flexibility-requirements-section">
      <Flex dir="row" align="center" mb="8px" gridGap={2}>
        <Text fontSize="14px" fontWeight={700} data-testid="flexibility-requirements-title">
          {t('requisition:flexibilityRequirements')}
        </Text>
        <Text fontSize="12px" fontWeight={400} data-testid="flexibility-requirements-title-2">
          {t('requisition:flexibilityOutOf')}
        </Text>
      </Flex>
      <Box overflowX="auto" width="100%">
        <GenericDataTable columns={columns} data={data} getCellBgColor={getCellBgColor} />
      </Box>
    </VStack>
  );
});

FlexibilityRequirement.displayName = 'FlexibilityRequirement';
